/** `/packages` uses a string Stripe sub status; legacy `/get/packages` nests invoice fields. */
export type SubscriptionPackage = {
  payment_status?:
    | string
    | null
    | {
        subscription: string;
        customer: string;
        currency: string;
        subtotal: number;
        period_end: number;
        status?: string;
      };
};

const isPresentId = (value: unknown): boolean =>
  Boolean(value && value !== 'undefined' && value !== 'null');

type BillingIdSource = {
  subId?: string | null;
  subscription?: string | null;
  custId?: string | null;
  customer?: string | null;
};

export const resolveUserBillingIds = (
  user: BillingIdSource | null | undefined
): { subId: string | null; custId: string | null } => ({
  subId: user?.subId || user?.subscription || null,
  custId: user?.custId || user?.customer || null,
});

export const getSubscriptionPaymentStatus = (pkg: SubscriptionPackage): string | null => {
  const raw = pkg?.payment_status;
  if (typeof raw === 'string') {
    return raw;
  }
  if (raw && typeof raw === 'object' && typeof raw.status === 'string') {
    return raw.status;
  }
  return null;
};

export const hasLiveSubscription = (packages: SubscriptionPackage[]): boolean =>
  Array.isArray(packages) &&
  packages.some(pkg => {
    const status = getSubscriptionPaymentStatus(pkg);
    return status === 'active' || status === 'trialing';
  });

/**
 * True when every subscription in a `/packages` payload has ended in Stripe (`canceled`).
 * Such accounts fall back to the Free plan; past_due/unpaid still need the payment-failed flow.
 */
export const hasOnlyCanceledSubscriptions = (packages: SubscriptionPackage[]): boolean =>
  Array.isArray(packages) &&
  packages.length > 0 &&
  packages.every(pkg => getSubscriptionPaymentStatus(pkg) === 'canceled');

const hasStalePaidBillingMetadata = (billing: {
  subId?: string | null;
  custId?: string | null;
}): boolean => isPresentId(billing.custId) || isPresentId(billing.subId);

/**
 * User should complete the free-plan path (Auth0 metadata + local session) when Stripe shows
 * no live subscription but the account still carries paid-plan billing ids.
 */
export const shouldDowngradeToFreePlan = (
  packages: SubscriptionPackage[],
  billing: { subId?: string | null; custId?: string | null; free?: boolean }
): boolean => {
  if (billing.free === true) {
    return false;
  }
  if (hasLiveSubscription(packages)) {
    return false;
  }
  if (hasOnlyCanceledSubscriptions(packages)) {
    return true;
  }
  if (Array.isArray(packages) && packages.length === 0 && hasStalePaidBillingMetadata(billing)) {
    return true;
  }
  return false;
};

export const FREE_PLAN_COMPLETION_PATH = '/created?session_id=free';
